import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import { CircularProgress, Divider, Stack, Typography } from "@mui/material";
import { DialogContext, QuestionsContext, SnackbarContext } from "../../provider/MyContexts";
import { insertQuestionToExcel, resolveQuestionsDiff } from "../../commands/questions/export";
import { checkDiffAddinExcel, compareQuestionsValue } from "../../commands/questions/compare";
import MyCarousel from "../features/generate/elements/MyCarousel";
import { getQuestionIds } from "../../commands/questions/excelInfo";

export type DialogState = {
  open: boolean;
  title: string;
  content: string | JSX.Element;
  actions?: JSX.Element;
};

export default function MyDialog() {
  const { dialogState, handleDialogClose } = React.useContext(DialogContext);

  return (
    <Dialog
      open={dialogState.open}
      onClose={handleDialogClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        padding: 2,
        minWidth: 300,
      }}
    >
      <DialogTitle id="alert-dialog-title">
        <Typography variant="label_M">{dialogState.title}</Typography>
      </DialogTitle>
      <DialogContent>
        {typeof dialogState.content === "string" ? (
          <DialogContentText id="alert-dialog-description">{dialogState.content}</DialogContentText>
        ) : (
          dialogState.content
        )}
      </DialogContent>
      {dialogState.actions}
    </Dialog>
  );
}

export function QuestionsDiffDialog1(props: {
  open: boolean;
  handleClose: () => void;
  handleDialogStep?: (step: number) => void;
}) {
  const { questions, editSheet } = React.useContext(QuestionsContext);

  const { handleSnackbarOpen } = React.useContext(SnackbarContext);
  const [loading, setLoading] = React.useState(false);
  const [diffs, setDiffs] = React.useState<any>(null);

  async function handleResolve() {
    // ここでは、設問レベルの差分を解消する
    setLoading(true);
    const { onlyInAddin, onlyInExcel } = await checkDiffAddinExcel(questions, editSheet);

    await resolveQuestionsDiff(questions, editSheet, onlyInAddin, onlyInExcel);
    setLoading(false);
    handleSnackbarOpen("success", "正常にシンクしました");
  }

  async function updateDiffs() {
    if (questions.length && editSheet) {
      const { onlyInAddin, onlyInExcel, orderMap, addin, excel, moveStep } =
        await checkDiffAddinExcel(questions, editSheet);
      setDiffs({
        diff: onlyInAddin.length > 0 || onlyInExcel.length > 0 || moveStep.length > 0,
        onlyInAddin,
        onlyInExcel,
        orderMap,
        addin,
        excel,
      });
    }
  }

  React.useEffect(() => {
    if (!loading && props.open) {
      updateDiffs().catch((error) => {
        console.error("Error updating diffs:", error);
        handleSnackbarOpen("error", "差分の取得に失敗しました");
      });
    }
  }, [loading, props.open, questions, editSheet]);

  if (!props.open) {
    return <></>;
  }

  return (
    <Dialog
      open={props.open}
      // onClose={props.handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        padding: 2,
        minWidth: 300,
      }}
    >
      <DialogTitle id="alert-dialog-title">
        <Typography
          variant="body_L"
          sx={{
            fontWeight: 500,
          }}
        >
          {"AddinとExcelの差分（設問ID）"}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading || !diffs ? (
          <Stack
            flex={1}
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="100%"
            minHeight={100}
          >
            <CircularProgress size={"2rem"} />
          </Stack>
        ) : (
          generateDialogContent(diffs.onlyInAddin, diffs.onlyInExcel, diffs.orderMap, diffs.excel)
        )}
      </DialogContent>
      <DialogActions
        sx={{
          marginBottom: 2,
        }}
      >
        <Button variant="outlined" onClick={() => props.handleClose()}>
          <Typography variant="button">とじる</Typography>
        </Button>
        {diffs && diffs.diff ? (
          <Button
            variant="contained"
            onClick={async () => handleResolve()}
            loading={loading}
            loadingPosition="start"
          >
            <Typography variant="button">Excelに反映する</Typography>
          </Button>
        ) : (
          <Button variant="contained" onClick={() => props.handleDialogStep(2)}>
            <Typography variant="button">チェックを進める</Typography>
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export function generateDialogContent(
  onlyInAddin: string[],
  onlyInExcel: string[],
  orderMap: number[][],
  excel: string[]
): JSX.Element {
  const orderDiff =
    orderMap.filter((i) => i[1] !== undefined).filter((i) => i[0] !== i[1]).length > 0;

  return (
    <Stack direction={"column"} spacing={2} width={"100%"}>
      {onlyInExcel.length ? (
        <Stack>
          <Typography variant="body_M">Excelにのみ存在</Typography>
          <Divider flexItem sx={{ marginY: 1 }} />
          <Typography marginLeft={0.5} variant="body_S">
            {onlyInExcel.join(", ")}
          </Typography>
        </Stack>
      ) : (
        <></>
      )}
      {onlyInAddin.length ? (
        <Stack>
          <Typography variant="body_M"> Add-inにのみ存在</Typography>
          <Divider flexItem sx={{ marginY: 1 }} />
          <Typography marginLeft={0.5} variant="body_S">
            {onlyInAddin.join(", ")}
          </Typography>
        </Stack>
      ) : (
        <></>
      )}
      {orderDiff ? (
        <Stack>
          <Typography variant="body_M">設問順の不一致</Typography>
          <Divider flexItem sx={{ marginY: 1 }} />
          <Stack marginLeft={0.5} spacing={1}>
            {orderMap.map((arr, _) => {
              const addInPlace = arr[1] !== undefined ? `${arr[1] + 1}番目` : "存在しません";
              if (arr[0] === arr[1]) {
                return <></>;
              }

              return (
                <Typography
                  key={_}
                  variant="body_S"
                >{`${excel[_]}: ${arr[0] + 1}番目 → ${addInPlace}`}</Typography>
              );
            })}
          </Stack>
        </Stack>
      ) : (
        <></>
      )}
      {!onlyInExcel.length && !onlyInAddin.length && !orderDiff ? (
        <Stack>
          <Typography variant="body_M">差分は見つかりませんでした。</Typography>
        </Stack>
      ) : (
        <Typography variant="body_S" align="center" marginTop={2}>
          Excel反映をしますか
        </Typography>
      )}
    </Stack>
  );
}

// export function generateDialogActions(contents: any[]) {
//   return (
//     <DialogActions>
//       {contents.map((content, index) => (
//         <Button
//           key={index}
//           onClick={() => {
//             content.onClick();
//           }}
//           disabled={content.disabled == undefined ? false : content.disabled}
//           sx={{
//             fontSize: "0.8rem",
//           }}
//         >
//           {content.label}
//         </Button>
//       ))}
//     </DialogActions>
//   );
// }

export function QuestionsDiffDialog2(props: {
  open: boolean;
  handleClose: () => void;
  dialogStep: number;
  handleDialogStep?: (step: number) => void;
}) {
  const { questions, editSheet } = React.useContext(QuestionsContext);

  const { handleSnackbarOpen } = React.useContext(SnackbarContext);
  const [loading, setLoading] = React.useState(false);
  const [diffs, setDiffs] = React.useState<any[] | null>(null);
  const [isResolved, setIsResolved] = React.useState(false);

  async function handleResolve() {
    // ここでは、設問の中レベルの差分を解消する

    if (diffs && diffs.length) {
      setLoading(true);

      for (const diff of diffs) {
        console.log(diff);

        const { excel } = await getQuestionIds([], editSheet);
        const targetIndex = excel.findIndex((q) => q === diff.questionId);

        const nextQuestionId = excel[targetIndex + 1];

        const targetQuestion = questions.find((q) => q.questionId === diff.questionId);
        const nextQuestion = nextQuestionId
          ? questions.find((q) => q.questionId === nextQuestionId)
          : undefined;

        await insertQuestionToExcel(editSheet, targetQuestion, nextQuestion);
      }
      setLoading(false);
    }

    handleSnackbarOpen("success", "正常にシンクしました");
    setIsResolved(true);
  }

  async function updateDiffs() {
    if (editSheet && questions.length) {
      setLoading(true);
      const diffTmp = await compareQuestionsValue(editSheet, questions);
      setDiffs(diffTmp);
      setLoading(false);
    }
  }

  React.useEffect(() => {
    if (props.open && props.dialogStep === 2) {
      updateDiffs().catch((error) => {
        console.error("Error updating diffs:", error);
        handleSnackbarOpen("error", "差分の取得に失敗しました");
      });
    }
  }, [props.open, editSheet, questions]);

  if (!props.open) {
    return <></>;
  }

  return (
    <Dialog
      open={props.open}
      // onClose={props.handleClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      sx={{
        padding: 2,
        minWidth: 300,
      }}
    >
      <DialogTitle id="alert-dialog-title">
        <Typography
          variant="body_L"
          sx={{
            fontWeight: 500,
          }}
        >
          {"AddinとExcelの差分（設問の中身）"}
        </Typography>
      </DialogTitle>
      <DialogContent
        sx={{
          overflowX: "hidden",
        }}
      >
        {loading || !diffs ? (
          <Stack
            flex={1}
            alignItems="center"
            justifyContent="center"
            width="100%"
            height="100%"
            minHeight={100}
          >
            <CircularProgress size={"2rem"} />
          </Stack>
        ) : (
          <MyCarousel items={diffs} isResolved={isResolved} />
        )}
      </DialogContent>
      <DialogActions
        sx={{
          marginBottom: 2,
        }}
      >
        <Button variant="outlined" onClick={() => props.handleClose()}>
          とじる
        </Button>
        {diffs && diffs.length ? (
          <Button
            variant="contained"
            onClick={async () => handleResolve()}
            loading={loading}
            loadingPosition="start"
            disabled={!isResolved}
          >
            Excelに反映する
          </Button>
        ) : (
          <></>
        )}
      </DialogActions>
    </Dialog>
  );
}
